import React from "react";
import {
  IconX,
  IconEdit,
  IconBuilding,
  IconId,
  IconPhone,
  IconMail,
  IconUserCheck,
  IconContract,
  IconBuildingCastle,
  IconMapPin,
  IconFileText,
  IconLocation,
  IconUser,
} from "@tabler/icons-react";
import ButtonStyled from "../../../components/common/ButtonStyled";
import { useLanguage } from "../../../context/LanguageContext";

interface OutletPreviewModalProps {
  outletData: {
    outletName?: string;
    code?: string;
    email?: string;
    mobileNo?: string;
    dialCode?: string;
    cityName?: string;
    districtName?: string;
    countryName?: string;
    areaCode?: string;
    address?: string;
    shortAddress?: string;
    nationalAddress?: string;
    latitude?: string | number;
    longitude?: string | number;
    status?: string;
    createdAt?: string;
    warehouseName?: string;
    warehouseId?: string;
    managerName?: string;
    managerEmail?: string;
    managerPhone?: string;
  };
  onClose: () => void;
  onEdit?: () => void;
  onBack?: () => void;
  onSubmit?: React.MouseEventHandler<HTMLButtonElement>;
  isSubmitting?: boolean;
  mode?: "view" | "confirm";
  isSidebar?: boolean;
}

const SectionHeader = ({
  icon: Icon,
  title,
  className = "border-b border-[#FBD037]/30 pb-2 mb-3 ",
}: {
  icon: any;
  title: string;
  className?: string;
}) => {
  const { t } = useLanguage();
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-semibold tracking-wide">
        {t(title)}
      </p>
    </div>
  );
};

const OutletPreviewModal: React.FC<OutletPreviewModalProps> = ({
  outletData,
  onClose,
  onEdit,
  onBack,
  onSubmit,
  isSubmitting = false,
  mode = "view",
  isSidebar = false,
}) => {
  const { t } = useLanguage();

  const personalRows = [
    { label: t("Outlet Name"), value: outletData.outletName },
    { label: t("Outlet Code"), value: outletData.code },
    { label: t("Warehouse"), value: outletData.warehouseName },
    {
      label: t("Mobile Number"),
      value:
        outletData.dialCode && outletData.mobileNo
          ? `+${outletData.dialCode} ${outletData.mobileNo}`
          : outletData.mobileNo,
      icon: <IconPhone size={18} />,
    },
    {
      label: t("Email"),
      value: outletData.email,
      icon: <IconMail size={18} />,
    },
    {
      label: t("Created Date"),
      value: outletData.createdAt
        ? new Date(outletData.createdAt).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : undefined,
      icon: <IconContract size={18} />,
    },
  ];

  const locationRows = [
    {
      label: t("Country"),
      value: outletData.countryName,
      icon: <IconBuildingCastle size={18} />,
    },
    {
      label: t("City"),
      value: outletData.cityName,
      icon: <IconBuilding size={18} />,
    },
    {
      label: t("District"),
      value: outletData.districtName,
      icon: <IconMapPin size={18} />,
    },
    {
      label: t("Latitude"),
      value: outletData.latitude,
      icon: <IconLocation size={18} />,
    },
    {
      label: t("Longitude"),
      value: outletData.longitude,
      icon: <IconLocation size={18} />,
    },
    {
      label: t("Short Address"),
      value: outletData.shortAddress,
      icon: <IconFileText size={18} />,
    },
    {
      label: t("Address"),
      value: outletData.address,
      icon: <IconMapPin size={18} />,
    },
  ];

  const Row = ({
    label,
    value,
    className,
  }: {
    label: string;
    className?: string;
    value: React.ReactNode;
  }) => (
    <div className={`flex items-center gap-2 relative z-10 ${className || ""}`}>
      <div className="flex items-center gap-2">
        <span className="font-medium text-sm text-[#212121]">{label}: </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="break-all text-xs text-[#212121] text-right font-medium">
          {value !== undefined && value !== null && value !== "" ? value : "-"}
        </span>
      </div>
    </div>
  );

  const Card = ({
    title,
    icon,
    rows,
  }: {
    title: string;
    icon: any;
    rows: { label: string; value?: React.ReactNode; icon?: React.ReactNode }[];
  }) => (
    <div className="group relative rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 flex-1 overflow-hidden">
      <div className="flex items-center justify-between border-b border-[#FBD037]/30 pb-2 mb-3">
        <SectionHeader icon={icon} title={title} className="" />
        {title === t("Personal Details") && (
          <span
            className={`px-2.5 py-0.5 text-[10px] tracking-wide rounded-full font-semibold inline-block ${
              outletData.status === "ACTIVE"
                ? "bg-green-100 text-green-700 border border-green-200"
                : "bg-red-100 text-red-600 border border-red-200"
            }`}
          >
            {t(outletData.status || "")}
          </span>
        )}
      </div>
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
      <div className="grid grid-cols-3 gap-y-2">
        {rows.map((row) => (
          <Row key={row.label} label={row.label} value={row.value}   className={row.label === t("Address") ? "col-span-3" : ""}/>
        ))}
      </div>
    </div>
  );

  if (isSidebar) {
    return (
      <div className="flex flex-col h-full bg-white/20 rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center flex-shrink-0 justify-between  px-6 py-4 border-b border-gray-200 rounded-t-3xl">
          <h1 className="text-xl font-bold text-[#212121]">
            {t("Outlet Details")}
          </h1>
          <div className="flex items-center gap-2">
            {mode === "view" && onEdit && (
              <button
                type="button"
                onClick={onEdit}
                className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 bg-white"
                title={t("Edit")}
              >
                <IconEdit size={20} />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 bg-white"
              title={t("Close")}
            >
              <IconX size={20} />
            </button>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="w-full flex-1 overflow-y-auto  min-h-0">
          <div className="space-y-6 p-6 scrollbar-clean">
            {mode === "confirm" && (
              <div className="flex mb-6 space-x-2">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="w-1/3 border-b-4 border-yellow-400 rounded-t-md"
                  />
                ))}
              </div>
            )}

            <div className="flex flex-col gap-6">
              <Card
                title={t("Personal Details")}
                icon={IconUser}
                rows={personalRows}
              />
              <Card
                title={t("Location Details")}
                icon={IconMapPin}
                rows={locationRows}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        {(onBack || (mode === "confirm" && onSubmit)) && (
          <div className="border-t border-[#D8D8D8] px-6 py-4 flex justify-end space-x-2 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-b-3xl flex-shrink-0">
            {onBack && (
              <button
                onClick={onBack}
                className="px-5 py-2 text-sm rounded-full border border-gray-300 hover:bg-gray-100 bg-white"
              >
                {t("Previous")}
              </button>
            )}

            {mode === "confirm" && onSubmit && (
              <ButtonStyled
                value={t("Create Outlet")}
                onClick={onSubmit}
                isPending={isSubmitting}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-end p-4">
        <div className="rounded-2xl  xl:w-7/12 lg:w-8/12 w-11/12 relative">
          <div className="flex items-center">
            <div className=" bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex items-center justify-between px-6 py-4   w-full rounded-tl-3xl rounded-tr-[63px] ">
              <h1 className="text-2xl"> Outlet Details</h1>
            </div>
            <div className="relative ">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={38}
                height={37}
                viewBox="0 0 38 37"
                fill="none"
                className="absolute -bottom-[10px] -left-[8px] rotate-[-1.8deg]"
              >
                <path
                  d="M0.5 0L4.86189 10.5496C10.4737 24.1222 22.9306 33.645 37.5 35.5L4.91509 37L0.5 0Z"
                  fill="#F7F6F2"
                />
              </svg>

              {mode === "view" && onEdit && (
                <button
                  onClick={onEdit}
                  className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer w-12 h-12 ml-5 flex items-center justify-center flex-shrink-0"
                  title="Edit"
                >
                  <IconEdit size={20} />
                </button>
              )}
              <button
                onClick={onClose}
                className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer w-12 h-12 ml-5 flex items-center justify-center flex-shrink-0"
              >
                <IconX size={20} />
              </button>
            </div>
          </div>
          <div className="w-full bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-tr-[30px]">
            <div className="h-[calc(100vh-10rem)] overflow-y-auto space-y-6 p-6 pt-2 scrollbar-clean">
              {mode === "confirm" && (
                <div className="flex mb-6 space-x-2">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="w-1/3 border-b-4 border-yellow-400 rounded-t-md"
                    />
                  ))}
                </div>
              )}

              <div className=" ">
                <Card
                  title="Personal Details"
                  icon={IconUser}
                  rows={personalRows}
                />
                <Card
                  title="Location Details"
                  icon={IconMapPin}
                  rows={locationRows}
                />
              </div>
            </div>
          </div>

          {(onBack || (mode === "confirm" && onSubmit)) && (
            <div className="border-t border-[#D8D8D8] px-6 py-4 flex justify-end space-x-2  bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-b-[30px] ">
              {onBack && (
                <button
                  onClick={onBack}
                  className="px-5 py-2 text-sm rounded-full border border-gray-300 hover:bg-gray-100"
                >
                  Previous
                </button>
              )}

              {mode === "confirm" && onSubmit && (
                <ButtonStyled
                  value="Create Outlet"
                  onClick={onSubmit}
                  isPending={isSubmitting}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OutletPreviewModal;
